import type { LucideIcon } from 'lucide-react';
import { type JSX, useEffect, useState } from 'react';

import { cn } from '../lib/cn';
import { useDismissableOverlay } from '../lib/use-dismissable-overlay';
import { Button, type ButtonProps } from './button';

export interface ActionBarAction {
  label: string;
  onClick: () => void;
  icon?: LucideIcon;
  variant?: ButtonProps['variant'];
  disabled?: boolean;
}

export interface ActionBarProps {
  primary: ActionBarAction;
  /** Collapsed behind "Más acciones" when there is one or more. */
  secondary?: ActionBarAction[];
  menuLabel?: string;
  className?: string;
}

/**
 * Primary `Button` `md` always visible. When `secondary` has entries, a
 * second `Button variant="secondary"` opens a `role="menu"` popover with
 * one `role="menuitem"` per action, reusing the same `useDismissableOverlay`
 * Modal already relies on (trapped focus, Escape, outside click).
 */
function toButtonProps(action: ActionBarAction): Omit<ButtonProps, 'children' | 'size'> {
  return {
    onClick: action.onClick,
    ...(action.variant !== undefined ? { variant: action.variant } : {}),
    ...(action.disabled !== undefined ? { disabled: action.disabled } : {}),
    ...(action.icon !== undefined ? { icon: action.icon } : {}),
  };
}

export function ActionBar({
  primary,
  secondary = [],
  menuLabel = 'Más acciones',
  className,
}: ActionBarProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const { containerRef } = useDismissableOverlay({ open, onClose: () => setOpen(false) });

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent): void {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, containerRef]);

  return (
    <div className={cn('relative inline-flex items-center gap-2', className)}>
      <Button size="md" {...toButtonProps(primary)}>
        {primary.label}
      </Button>

      {secondary.length > 0 ? (
        <div ref={containerRef}>
          <Button
            type="button"
            size="md"
            variant="secondary"
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            {menuLabel}
          </Button>

          {open ? (
            <div
              role="menu"
              className="absolute right-0 z-10 mt-2 w-48 rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
            >
              {secondary.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  role="menuitem"
                  disabled={action.disabled}
                  onClick={() => {
                    setOpen(false);
                    action.onClick();
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-50"
                >
                  {action.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
